import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import Select from "../components/SafeSelect";
import { useAdmin } from "../context/AdminContext";
import {
  formatDate,
  formatDateForInput,
  getStatusColor,
  formatStatusLabel,
  VEHICLE_TYPES,
  isVehicleReadyForAssignment,
  getVehicleEffectiveStatus,
} from "../utils/helpers";

const normalizeVehicleSource = (vehicleSource = "") => {
  const normalizedSource = String(vehicleSource || "")
    .trim()
    .toLowerCase();

  if (normalizedSource === "garage" || normalizedSource === "fleet") {
    return "warehouse";
  }

  return vehicleSource;
};

const resolveDriverUserId = (driver = null) =>
  driver?.userId || driver?.uid || driver?.id || "";

const findDriverByIdentifier = (driverList = [], identifier = "") => {
  if (!identifier) {
    return null;
  }

  return (
    driverList.find(
      (driver) =>
        driver.id === identifier ||
        driver.userId === identifier ||
        driver.uid === identifier
    ) || null
  );
};

const TRANSPORT_TASK_TYPE = "transport";
const INSPECTION_TASK_TYPE = "vehicle_inspection";
const VEHICLE_SWAP_TASK_TYPE = "vehicle_swap";

const TASK_TYPE_OPTIONS = [
  { value: TRANSPORT_TASK_TYPE, label: "Transport Task" },
  { value: INSPECTION_TASK_TYPE, label: "Vehicle Inspection Task" },
  { value: "vehicle_swap", label: "Vehicle Swap" },
  { value: "vehicle_servicing", label: "Vehicle Servicing" },
  { value: "tire_maintenance", label: "Tire Maintenance" },
  { value: "custom_task", label: "Custom Task" },
];

const TASK_TYPE_LABELS = TASK_TYPE_OPTIONS.reduce(
  (labelsMap, taskTypeOption) => ({
    ...labelsMap,
    [taskTypeOption.value]: taskTypeOption.label,
  }),
  {}
);

const REMINDER_BEFORE_OPTIONS = [15, 30, 45, 60, 120, 180, 240, 1440];
const REMINDER_REPEAT_OPTIONS = [15, 30, 60, 120, 240, 720, 1440, 2880];
const PRIORITY_DEFAULT_REPEAT_MINUTES = {
  high: 15,
  medium: 120,
  low: 2880,
};

const getTaskTypeLabel = (taskType = TRANSPORT_TASK_TYPE) =>
  TASK_TYPE_LABELS[taskType] || "Custom Task";

const getVehicleDisplayLabel = (vehicle = null, fallback = "Vehicle") => {
  if (!vehicle) {
    return fallback;
  }

  const type = vehicle.vehicleType || vehicle.type || fallback;
  const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(" ");
  const descriptor = makeModel ? `${type} - ${makeModel}` : type;
  const plate = vehicle.licensePlate || vehicle.plateNumber || "";

  return plate ? `${descriptor} (${plate})` : descriptor;
};

const getOwnVehicleDisplayLabel = (driver = null) => {
  if (!driver) {
    return "Driver Own Vehicle";
  }

  const ownerName = driver.name || "Driver";
  const ownVehicleLabel = getVehicleDisplayLabel(
    driver.ownVehicle,
    "Own Vehicle"
  );
  return `${ownerName} Own Vehicle - ${ownVehicleLabel}`;
};

const getTaskVehicleLabel = ({ vehicleSource = "", driver = null, vehicle = null }) => {
  const normalizedSource = normalizeVehicleSource(vehicleSource);

  if (normalizedSource === "own") {
    return getOwnVehicleDisplayLabel(driver);
  }

  return getVehicleDisplayLabel(vehicle, "Fleet Vehicle");
};

const formatReminderIntervalLabel = (minutesValue) => {
  const totalMinutes = Number.parseInt(String(minutesValue ?? ""), 10);

  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
    return `${minutesValue} min`;
  }

  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const remainingMinutes = totalMinutes % 60;
  const parts = [];

  if (days > 0) {
    parts.push(`${days} day${days === 1 ? "" : "s"}`);
  }

  if (hours > 0) {
    parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
  }

  if (remainingMinutes > 0) {
    parts.push(`${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}`);
  }

  return parts.join(" ");
};

const getDriverIdentifierSet = (driver = null) =>
  new Set(
    [driver?.userId, driver?.uid, driver?.id]
      .filter(Boolean)
      .map((identifier) => String(identifier).trim())
  );

const resolveAssignedFleetVehicle = (driver = null, fleetVehicles = []) => {
  if (!driver) {
    return null;
  }

  const assignedVehicleId =
    driver?.assignedVehicle?.id || driver?.assignedVehicleId;

  if (assignedVehicleId) {
    const matchedVehicle = fleetVehicles.find(
      (vehicle) => vehicle.id === assignedVehicleId
    );
    if (matchedVehicle) {
      return matchedVehicle;
    }
  }

  const identifiers = getDriverIdentifierSet(driver);
  const matchedByDriver = fleetVehicles.find((vehicle) => {
    const assignedDriver = String(vehicle?.assignedDriver || "").trim();
    return assignedDriver && identifiers.has(assignedDriver);
  });

  return matchedByDriver || driver?.assignedVehicle || null;
};

const canDriverUseFleetVehicle = (
  vehicle = null,
  driver = null,
  activeTasks = []
) => {
  if (!vehicle || !driver) {
    return false;
  }

  const assignedDriver = String(vehicle.assignedDriver || "").trim();
  const identifiers = getDriverIdentifierSet(driver);
  if (assignedDriver && identifiers.has(assignedDriver)) {
    return true;
  }

  return isVehicleReadyForAssignment(vehicle, activeTasks, { lockHours: 4 });
};

const getAutoVehicleSelectionForDriver = (
  driver = null,
  fleetVehicles = [],
  activeTasks = []
) => {
  const assignedFleetVehicle = resolveAssignedFleetVehicle(driver, fleetVehicles);
  const canUseAssignedFleetVehicle =
    Boolean(assignedFleetVehicle?.id) &&
    canDriverUseFleetVehicle(assignedFleetVehicle, driver, activeTasks);

  if (canUseAssignedFleetVehicle) {
    return {
      choice: "warehouse",
      vehicleId: assignedFleetVehicle.id,
      vehicleSource: "warehouse",
      autoHint: "fleet",
    };
  }

  if (driver?.ownVehicle) {
    return {
      choice: "own",
      vehicleId: resolveDriverUserId(driver) || driver?.id || "",
      vehicleSource: "own",
      autoHint: "own",
    };
  }

  return {
    choice: "",
    vehicleId: "",
    vehicleSource: "",
    autoHint: "",
  };
};

const Tasks = () => {
  const { tasks, drivers, vehicles, createTask, updateTask, deleteTask } =
    useAdmin();
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicleChoice, setSelectedVehicleChoice] = useState(""); // 'own' or 'warehouse'
  const [autoSelectedVehicleHint, setAutoSelectedVehicleHint] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formData, setFormData] = useState({
    taskType: TRANSPORT_TASK_TYPE,
    driverId: "",
    vehicleId: "",
    vehicleSource: "", // 'own' or 'warehouse'
    swapVehicleId: "",
    pickupLocation: "",
    dropoffLocation: "",
    startTime: "",
    endTime: "",
    description: "",
    priority: "medium",
    passengers: 1,
    vehicleType: "Any",
    inspectionVehicleId: "",
    inspectionVehicleSource: "", // 'own' or 'warehouse'
    inspectionDetails: "",
    inspectionDate: "",
    reminderEnabled: "yes",
    reminderMinutesBefore: "15",
    reminderRepeatMinutes: "120",
  });

  // Get only approved and available drivers
  const availableDrivers = drivers.filter((d) => {
    const isApproved = d.approved;
    
    let isAvailable = false;
    let availabilityInfo = 'Not set';
    
    // Check new availabilitySchedule system
    if (d.availabilitySchedule && Array.isArray(d.availabilitySchedule)) {
      const now = new Date();
      isAvailable = d.availabilitySchedule.some(avail => {
        if (!avail.isAvailable) return false;
        const endDate = avail.endDate?.toDate ? avail.endDate.toDate() : new Date(avail.endDate);
        return endDate > now;
      });
      availabilityInfo = isAvailable ? 'Available (Schedule)' : 'Not available';
    } else {
      // Fallback to availableUntil (old system)
      const availableUntilDate = d.availableUntil
        ? new Date(d.availableUntil.toDate())
        : null;
      isAvailable = !availableUntilDate || availableUntilDate > new Date();
      availabilityInfo = availableUntilDate?.toLocaleString() || 'Not set';
    }

    console.log(`Driver ${d.name}:`, {
      approved: isApproved,
      availabilityInfo: availabilityInfo,
      currentTime: new Date().toLocaleString(),
      isAvailable: isAvailable,
      hasOwnVehicle: !!d.ownVehicle,
      included: isApproved && isAvailable,
    });

    return isApproved && isAvailable;
  });

  const inspectionVehicleSelectValue =
    formData.inspectionVehicleSource && formData.inspectionVehicleId
      ? `${formData.inspectionVehicleSource}:${formData.inspectionVehicleId}`
      : "";

  const isInspectionTask = formData.taskType === INSPECTION_TASK_TYPE;
  const isTransportTask = formData.taskType === TRANSPORT_TASK_TYPE;
  const isVehicleSwapTask = formData.taskType === VEHICLE_SWAP_TASK_TYPE;
  const isMaintenanceStyleTask = !isInspectionTask && !isTransportTask;
  const selectedDriverUserId = resolveDriverUserId(selectedDriver);
  const selectedDriverIdentifierSet = getDriverIdentifierSet(selectedDriver);
  const selectedDriverAssignedFleetVehicle = resolveAssignedFleetVehicle(
    selectedDriver,
    vehicles
  );
  const selectedDriverAssignedFleetVehicleAvailable =
    Boolean(selectedDriverAssignedFleetVehicle?.id) &&
    canDriverUseFleetVehicle(
      selectedDriverAssignedFleetVehicle,
      selectedDriver,
      tasks
    );
  const selectedDriverOwnVehicleAvailable = Boolean(selectedDriver?.ownVehicle);
  const reminderEnabled = String(formData.reminderEnabled || "yes") !== "no";
  const reminderMinutesValue = Number.parseInt(
    String(formData.reminderMinutesBefore || ""),
    10
  );
  const reminderRepeatMinutesValue = Number.parseInt(
    String(formData.reminderRepeatMinutes || ""),
    10
  );

  const warehouseVehicleOptions = vehicles.filter((vehicle) => {
    const assignedDriver = String(vehicle.assignedDriver || "").trim();
    const isSelectedVehicle =
      Boolean(formData.vehicleId) && formData.vehicleId === vehicle.id;
    const isAssignedToSelectedDriver =
      Boolean(assignedDriver) &&
      selectedDriverIdentifierSet.has(assignedDriver);

    if (isSelectedVehicle || isAssignedToSelectedDriver) {
      return true;
    }

    return isVehicleReadyForAssignment(vehicle, tasks, {
      lockHours: 4,
    });
  });

  const warehouseVehicleSelectOptions = warehouseVehicleOptions.filter(
    (vehicle) => {
      if (!isTransportTask) {
        return true;
      }

      const seats = Number.parseInt(vehicle.capacity || vehicle.seats || 0, 10);
      const passengerCount = Number.parseInt(formData.passengers || 1, 10);

      if (Number.isFinite(seats) && seats > 0 && seats < passengerCount) {
        return false;
      }

      if (formData.vehicleType === "Any") {
        return true;
      }

      const type = String(vehicle.vehicleType || vehicle.type || "").toLowerCase();
      return type === String(formData.vehicleType || "").toLowerCase();
    }
  );

  const selectedCurrentTaskVehicle =
    formData.vehicleSource === "own"
      ? selectedDriver?.ownVehicle
      : vehicles.find((vehicle) => vehicle.id === formData.vehicleId);

  const currentVehicleLabel = getTaskVehicleLabel({
    vehicleSource: formData.vehicleSource,
    driver: selectedDriver,
    vehicle: selectedCurrentTaskVehicle,
  });

  const swapVehicleSelectOptions = warehouseVehicleOptions.filter(
    (vehicle) => vehicle.id !== formData.vehicleId
  );

  const selectedSwapVehicle = vehicles.find(
    (vehicle) => vehicle.id === formData.swapVehicleId
  );

  const selectedSwapVehicleLabel = getVehicleDisplayLabel(
    selectedSwapVehicle,
    "Fleet Vehicle"
  );

  const isSubmitDisabled = isInspectionTask
    ? !formData.driverId ||
      !formData.inspectionVehicleId ||
      !formData.inspectionDate ||
      !formData.inspectionDetails.trim() ||
      (reminderEnabled &&
        (Number.isNaN(reminderMinutesValue) ||
          reminderMinutesValue <= 0 ||
          Number.isNaN(reminderRepeatMinutesValue) ||
          reminderRepeatMinutesValue <= 0))
    : !formData.driverId ||
      !formData.vehicleId ||
      !formData.startTime ||
      (isVehicleSwapTask && !formData.swapVehicleId) ||
      (reminderEnabled &&
        (Number.isNaN(reminderMinutesValue) ||
          reminderMinutesValue <= 0 ||
          Number.isNaN(reminderRepeatMinutesValue) ||
          reminderRepeatMinutesValue <= 0)) ||
      (isTransportTask
        ? !formData.pickupLocation.trim() ||
          !formData.dropoffLocation.trim() ||
          !formData.endTime
        : !formData.description.trim());

  // Debug: Log vehicles data
  React.useEffect(() => {
    console.log("=== DRIVERS DEBUG ===");
    console.log("Total drivers:", drivers.length);
    console.log("Available drivers:", availableDrivers.length);
    console.log(
      "Drivers with own vehicle:",
      drivers.filter((d) => d.ownVehicle).length
    );
    console.log(
      "Available drivers with own vehicle:",
      availableDrivers.filter((d) => d.ownVehicle).length
    );
    console.log("Total vehicles:", vehicles.length);
    console.log("Selectable warehouse vehicles:", warehouseVehicleOptions.length);
    console.log("Filtered vehicle options:", warehouseVehicleSelectOptions.length);
  }, [
    drivers,
    availableDrivers,
    vehicles,
    warehouseVehicleOptions,
    warehouseVehicleSelectOptions,
  ]);

  const handleOpenDialog = (task = null) => {
    setAutoSelectedVehicleHint("");

    if (task) {
      const normalizedVehicleSource = normalizeVehicleSource(task.vehicleSource);
      const nextTaskType = task.taskType || TRANSPORT_TASK_TYPE;
      const matchedDriver = findDriverByIdentifier(drivers, task.driverId);
      const resolvedDriverDocId =
        matchedDriver?.id || task.driverDocId || task.driverId || "";

      setEditMode(true);
      setSelectedTask(task);
      setSelectedDriver(matchedDriver);
      setSelectedVehicleChoice(
        nextTaskType !== INSPECTION_TASK_TYPE && normalizedVehicleSource
          ? normalizedVehicleSource
          : ""
      );
      setFormData({
        taskType: nextTaskType,
        driverId: resolvedDriverDocId,
        vehicleId: task.vehicleId || "",
        vehicleSource: normalizedVehicleSource || "",
        swapVehicleId:
          nextTaskType === VEHICLE_SWAP_TASK_TYPE ? task.swapVehicleId || "" : "",
        pickupLocation: task.pickupLocation || "",
        dropoffLocation: task.dropoffLocation || "",
        startTime: formatDateForInput(task.startTime),
        endTime: formatDateForInput(task.endTime),
        description: task.description || "",
        priority: task.priority || "medium",
        passengers: task.passengers || 1,
        vehicleType: task.vehicleType || "Any",
        inspectionVehicleId:
          nextTaskType === INSPECTION_TASK_TYPE ? task.vehicleId || "" : "",
        inspectionVehicleSource:
          nextTaskType === INSPECTION_TASK_TYPE ? normalizedVehicleSource || "" : "",
        inspectionDetails:
          task.inspectionDetails ||
          (nextTaskType === INSPECTION_TASK_TYPE ? task.description || "" : ""),
        inspectionDate: formatDateForInput(task.inspectionDate || task.startTime),
        reminderEnabled: task.reminderEnabled === false ? "no" : "yes",
        reminderMinutesBefore: String(task.reminderMinutesBefore ?? 15),
        reminderRepeatMinutes: String(
          task.reminderRepeatMinutes ??
            PRIORITY_DEFAULT_REPEAT_MINUTES[task.priority] ??
            120
        ),
      });
    } else {
      setEditMode(false);
      setSelectedTask(null);
      setSelectedDriver(null);
      setSelectedVehicleChoice("");
      setFormData({
        taskType: TRANSPORT_TASK_TYPE,
        driverId: "",
        vehicleId: "",
        vehicleSource: "",
        swapVehicleId: "",
        pickupLocation: "",
        dropoffLocation: "",
        startTime: "",
        endTime: "",
        description: "",
        priority: "medium",
        passengers: 1,
        vehicleType: "Any",
        inspectionVehicleId: "",
        inspectionVehicleSource: "",
        inspectionDetails: "",
        inspectionDate: "",
        reminderEnabled: "yes",
        reminderMinutesBefore: "15",
        reminderRepeatMinutes: "120",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedTask(null);
    setSelectedDriver(null);
    setSelectedVehicleChoice("");
    setAutoSelectedVehicleHint("");
  };

  const handleInputChange = (e) => {
    console.log("Input changed:", e.target.name, "=", e.target.value);

    const { name, value } = e.target;

    if (name === "taskType") {
      const autoSelection = getAutoVehicleSelectionForDriver(
        selectedDriver,
        vehicles,
        tasks
      );
      const autoVehicleId = autoSelection.vehicleId;
      const autoVehicleSource = autoSelection.vehicleSource;

      setAutoSelectedVehicleHint(autoSelection.autoHint || "");
      setSelectedVehicleChoice("");
      if (value !== INSPECTION_TASK_TYPE && autoSelection.choice) {
        setSelectedVehicleChoice(autoSelection.choice);
      }
      setFormData((previousFormData) => ({
        ...previousFormData,
        taskType: value,
        vehicleId: value !== INSPECTION_TASK_TYPE ? autoVehicleId : "",
        vehicleSource:
          value !== INSPECTION_TASK_TYPE ? autoVehicleSource : "",
        swapVehicleId: "",
        pickupLocation: "",
        dropoffLocation: "",
        startTime: "",
        endTime: "",
        description: "",
        inspectionVehicleId:
          value === INSPECTION_TASK_TYPE ? autoVehicleId : "",
        inspectionVehicleSource:
          value === INSPECTION_TASK_TYPE ? autoVehicleSource : "",
        inspectionDetails: "",
        inspectionDate: "",
        reminderEnabled: previousFormData.reminderEnabled || "yes",
        reminderMinutesBefore: previousFormData.reminderMinutesBefore || "15",
        reminderRepeatMinutes:
          previousFormData.reminderRepeatMinutes ||
          String(PRIORITY_DEFAULT_REPEAT_MINUTES.medium),
      }));
      return;
    }

    if (name === "vehicleId") {
      setFormData((previousFormData) => ({
        ...previousFormData,
        vehicleId: value,
        swapVehicleId:
          previousFormData.taskType === VEHICLE_SWAP_TASK_TYPE &&
          previousFormData.swapVehicleId === value
            ? ""
            : previousFormData.swapVehicleId,
      }));
      return;
    }

    if (name === "priority") {
      setFormData((previousFormData) => {
        const previousPriority = previousFormData.priority || "medium";
        const previousDefaultRepeat = String(
          PRIORITY_DEFAULT_REPEAT_MINUTES[previousPriority] ||
            PRIORITY_DEFAULT_REPEAT_MINUTES.medium
        );
        const nextDefaultRepeat = String(
          PRIORITY_DEFAULT_REPEAT_MINUTES[value] ||
            PRIORITY_DEFAULT_REPEAT_MINUTES.medium
        );
        const shouldAutoUpdateRepeat =
          !previousFormData.reminderRepeatMinutes ||
          previousFormData.reminderRepeatMinutes === previousDefaultRepeat;

        return {
          ...previousFormData,
          priority: value,
          reminderRepeatMinutes: shouldAutoUpdateRepeat
            ? nextDefaultRepeat
            : previousFormData.reminderRepeatMinutes,
        };
      });
      return;
    }

    setFormData((previousFormData) => ({
      ...previousFormData,
      [name]: value,
    }));
  };

  const handleDriverSelect = (driverId) => {
    const driver = findDriverByIdentifier(drivers, driverId);
    const autoSelection = getAutoVehicleSelectionForDriver(
      driver,
      vehicles,
      tasks
    );
    const autoVehicleId = autoSelection.vehicleId;
    const autoVehicleSource = autoSelection.vehicleSource;
    const inspectionMode = formData.taskType === INSPECTION_TASK_TYPE;

    setSelectedDriver(driver);
    setAutoSelectedVehicleHint(autoSelection.autoHint || "");
    setSelectedVehicleChoice(!inspectionMode ? autoSelection.choice : "");
    setFormData((previousFormData) => ({
      ...previousFormData,
      driverId,
      vehicleId: !inspectionMode ? autoVehicleId : "",
      vehicleSource: !inspectionMode ? autoVehicleSource : "",
      swapVehicleId: "",
      inspectionVehicleId: inspectionMode ? autoVehicleId : "",
      inspectionVehicleSource: inspectionMode ? autoVehicleSource : "",
    }));
  };

  const handleVehicleChoice = (choice) => {
    setSelectedVehicleChoice(choice);
    setAutoSelectedVehicleHint("");

    if (choice === "own" && selectedDriver?.ownVehicle) {
      // For own vehicle, we don't need vehicleId - it's stored in driver document
      setFormData((previousFormData) => ({
        ...previousFormData,
        vehicleId: selectedDriverUserId || selectedDriver.id,
        vehicleSource: "own",
        swapVehicleId:
          previousFormData.taskType === VEHICLE_SWAP_TASK_TYPE
            ? ""
            : previousFormData.swapVehicleId,
      }));
    } else if (choice === "warehouse") {
      setFormData((previousFormData) => ({
        ...previousFormData,
        vehicleId: "",
        vehicleSource: "warehouse",
        swapVehicleId:
          previousFormData.taskType === VEHICLE_SWAP_TASK_TYPE
            ? ""
            : previousFormData.swapVehicleId,
      }));
    }
  };

  const handleInspectionVehicleSelect = (value) => {
    setAutoSelectedVehicleHint("");
    const [source = "", vehicleId = ""] = String(value).split(":");
    setFormData((previousFormData) => ({
      ...previousFormData,
      inspectionVehicleSource: source,
      inspectionVehicleId: vehicleId,
    }));
  };

  const validateForm = () => {
    if (!formData.driverId) {
      showSnackbar("Please select a driver", "error");
      return false;
    }

    const reminderMinutes = Number.parseInt(
      String(formData.reminderMinutesBefore || ""),
      10
    );
    const reminderRepeatMinutes = Number.parseInt(
      String(formData.reminderRepeatMinutes || ""),
      10
    );

    if (
      reminderEnabled &&
      (Number.isNaN(reminderMinutes) || reminderMinutes <= 0)
    ) {
      showSnackbar("Reminder lead time must be greater than 0", "error");
      return false;
    }

    if (
      reminderEnabled &&
      (Number.isNaN(reminderRepeatMinutes) || reminderRepeatMinutes <= 0)
    ) {
      showSnackbar("Repeat reminder interval must be greater than 0", "error");
      return false;
    }

    if (formData.taskType === INSPECTION_TASK_TYPE) {
      if (!formData.inspectionVehicleId || !formData.inspectionVehicleSource) {
        showSnackbar("Please select a vehicle for inspection", "error");
        return false;
      }
      if (!formData.inspectionDetails.trim()) {
        showSnackbar("Inspection details are required", "error");
        return false;
      }
      if (!formData.inspectionDate) {
        showSnackbar("Inspection date is required", "error");
        return false;
      }

      const inspectionDate = new Date(formData.inspectionDate);
      if (Number.isNaN(inspectionDate.getTime())) {
        showSnackbar("Inspection date is invalid", "error");
        return false;
      }

      return true;
    }

    if (!selectedVehicleChoice) {
      showSnackbar(
        "Please select a vehicle option (own vehicle or Fleet)",
        "error"
      );
      return false;
    }
    if (!formData.vehicleId || formData.vehicleId.trim() === "") {
      if (selectedVehicleChoice === "warehouse") {
        showSnackbar(
          "Please select a Fleet vehicle from the dropdown",
          "error"
        );
      } else {
        showSnackbar("Please select a vehicle", "error");
      }
      return false;
    }
    if (!formData.vehicleSource) {
      showSnackbar("Vehicle source is required", "error");
      return false;
    }

    if (formData.taskType === VEHICLE_SWAP_TASK_TYPE) {
      if (!formData.swapVehicleId) {
        showSnackbar("Please select a swap vehicle", "error");
        return false;
      }

      if (
        formData.vehicleSource === "warehouse" &&
        formData.vehicleId === formData.swapVehicleId
      ) {
        showSnackbar("Current vehicle and swap vehicle must be different", "error");
        return false;
      }
    }

    if (!formData.startTime) {
      showSnackbar("Task date and time is required", "error");
      return false;
    }

    const startTimeDate = new Date(formData.startTime);
    if (Number.isNaN(startTimeDate.getTime())) {
      showSnackbar("Task date and time is invalid", "error");
      return false;
    }

    if (isTransportTask) {
      if (!formData.pickupLocation.trim()) {
        showSnackbar("Pickup location is required", "error");
        return false;
      }
      if (!formData.dropoffLocation.trim()) {
        showSnackbar("Drop-off location is required", "error");
        return false;
      }
      if (!formData.endTime) {
        showSnackbar("End time is required", "error");
        return false;
      }

      const endTimeDate = new Date(formData.endTime);
      if (Number.isNaN(endTimeDate.getTime())) {
        showSnackbar("End time is invalid", "error");
        return false;
      }

      if (endTimeDate <= startTimeDate) {
        showSnackbar("End time must be after start time", "error");
        return false;
      }
    }

    if (isMaintenanceStyleTask && !formData.description.trim()) {
      showSnackbar("Task details are required", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    console.log("=== SUBMIT STARTED ===");
    console.log("Form Data:", formData);
    console.log("Selected Vehicle Choice:", selectedVehicleChoice);
    console.log("Vehicle ID:", formData.vehicleId);
    console.log("Vehicle Source:", formData.vehicleSource);

    if (!validateForm()) return;

    // Additional safety check to prevent empty vehicle selection
    if (
      !isInspectionTask &&
      (!formData.vehicleId || formData.vehicleId.trim() === "")
    ) {
      console.error("VALIDATION FAILED: Empty vehicle ID");
      showSnackbar("Please select a valid vehicle before submitting", "error");
      return;
    }

    console.log("Validation passed, creating task data...");

    const formDriver = findDriverByIdentifier(drivers, formData.driverId);
    const activeDriver = selectedDriver || formDriver;
    const resolvedDriverUserId =
      resolveDriverUserId(activeDriver) || formData.driverId;
    const resolvedDriverDocId =
      selectedDriver?.id ||
      formDriver?.id ||
      selectedTask?.driverDocId ||
      "";

    let taskData;

    if (isInspectionTask) {
      const inspectionDate = new Date(formData.inspectionDate);
      const reminderMinutes = Math.max(
        0,
        Number.parseInt(formData.reminderMinutesBefore, 10) || 0
      );
      const reminderRepeatMinutes = Math.max(
        1,
        Number.parseInt(formData.reminderRepeatMinutes, 10) ||
          PRIORITY_DEFAULT_REPEAT_MINUTES[formData.priority] ||
          PRIORITY_DEFAULT_REPEAT_MINUTES.medium
      );
      const reminderAt = new Date(
        inspectionDate.getTime() - reminderMinutes * 60 * 1000
      );
      const inspectionWarehouseVehicle = vehicles.find(
        (vehicle) => vehicle.id === formData.inspectionVehicleId
      );
      const selectedInspectionVehicle =
        formData.inspectionVehicleSource === "own"
          ? activeDriver?.ownVehicle
          : inspectionWarehouseVehicle;

      const inspectionVehicleLabel =
        formData.inspectionVehicleSource === "own"
          ? `${activeDriver?.name || "Driver"} Own Vehicle`
          : `${
              selectedInspectionVehicle?.vehicleType ||
              selectedInspectionVehicle?.type ||
              "Vehicle"
            } - ${selectedInspectionVehicle?.make || ""} ${
              selectedInspectionVehicle?.model || ""
            }`.trim();

      taskData = {
        driverId: resolvedDriverUserId,
        driverDocId: resolvedDriverDocId || null,
        taskType: INSPECTION_TASK_TYPE,
        vehicleId: formData.inspectionVehicleId,
        vehicleSource: formData.inspectionVehicleSource,
        vehicleType:
          selectedInspectionVehicle?.vehicleType ||
          selectedInspectionVehicle?.type ||
          "Inspection",
        pickupLocation: "Vehicle Inspection",
        dropoffLocation: inspectionVehicleLabel || "Assigned Vehicle",
        startTime: inspectionDate,
        endTime: inspectionDate,
        inspectionDate,
        inspectionDetails: formData.inspectionDetails.trim(),
        reminderEnabled,
        reminderMinutesBefore: reminderMinutes,
        reminderRepeatMinutes,
        reminderAt,
        reminderSent: false,
        reminderEventsSent: [],
        lastReminderEvent: null,
        lastReminderAt: null,
        description: formData.inspectionDetails.trim(),
        priority: formData.priority,
        passengers: 1,
        assignedTime: new Date(),
      };
    } else {
      const taskStartDate = new Date(formData.startTime);
      const reminderMinutes = Math.max(
        0,
        Number.parseInt(formData.reminderMinutesBefore, 10) || 0
      );
      const reminderRepeatMinutes = Math.max(
        1,
        Number.parseInt(formData.reminderRepeatMinutes, 10) ||
          PRIORITY_DEFAULT_REPEAT_MINUTES[formData.priority] ||
          PRIORITY_DEFAULT_REPEAT_MINUTES.medium
      );
      const reminderAt = new Date(
        taskStartDate.getTime() - reminderMinutes * 60 * 1000
      );

      const selectedTaskVehicle =
        formData.vehicleSource === "own"
          ? activeDriver?.ownVehicle
          : vehicles.find((vehicle) => vehicle.id === formData.vehicleId);

      const selectedSwapTaskVehicle =
        formData.taskType === VEHICLE_SWAP_TASK_TYPE
          ? vehicles.find((vehicle) => vehicle.id === formData.swapVehicleId)
          : null;

      const selectedVehicleLabel = getTaskVehicleLabel({
        vehicleSource: formData.vehicleSource,
        driver: activeDriver,
        vehicle: selectedTaskVehicle,
      });

      const selectedSwapTaskVehicleLabel = getVehicleDisplayLabel(
        selectedSwapTaskVehicle,
        "Fleet Vehicle"
      );

      if (isTransportTask) {
        taskData = {
          driverId: resolvedDriverUserId,
          driverDocId: resolvedDriverDocId || null,
          vehicleId: formData.vehicleId,
          vehicleSource: formData.vehicleSource,
          pickupLocation: formData.pickupLocation,
          dropoffLocation: formData.dropoffLocation,
          description: formData.description,
          priority: formData.priority,
          passengers: formData.passengers,
          vehicleType: formData.vehicleType,
          taskType: TRANSPORT_TASK_TYPE,
          startTime: taskStartDate,
          endTime: new Date(formData.endTime),
          reminderEnabled,
          reminderMinutesBefore: reminderMinutes,
          reminderRepeatMinutes,
          reminderAt,
          reminderSent: false,
          reminderEventsSent: [],
          lastReminderEvent: null,
          lastReminderAt: null,
          assignedTime: new Date(),
        };
      } else {
        const taskTypeLabel = getTaskTypeLabel(formData.taskType);
        const isVehicleSwapTaskPayload =
          formData.taskType === VEHICLE_SWAP_TASK_TYPE;
        const swapDescription = `Swap ${selectedVehicleLabel} with ${selectedSwapTaskVehicleLabel}`;

        taskData = {
          driverId: resolvedDriverUserId,
          driverDocId: resolvedDriverDocId || null,
          vehicleId: formData.vehicleId,
          vehicleSource: formData.vehicleSource,
          pickupLocation: taskTypeLabel,
          dropoffLocation: selectedVehicleLabel || "Assigned Vehicle",
          description: formData.description.trim(),
          priority: formData.priority,
          passengers: 1,
          vehicleType:
            selectedTaskVehicle?.vehicleType ||
            selectedTaskVehicle?.type ||
            formData.vehicleType ||
            "Any",
          taskType: formData.taskType,
          startTime: taskStartDate,
          endTime: taskStartDate,
          reminderEnabled,
          reminderMinutesBefore: reminderMinutes,
          reminderRepeatMinutes,
          reminderAt,
          reminderSent: false,
          reminderEventsSent: [],
          lastReminderEvent: null,
          lastReminderAt: null,
          assignedTime: new Date(),
          ...(isVehicleSwapTaskPayload
            ? {
                swapVehicleId: formData.swapVehicleId,
                swapVehicleSource: "warehouse",
                swapCurrentVehicleLabel: selectedVehicleLabel,
                swapVehicleLabel: selectedSwapTaskVehicleLabel,
                swapSummary: `${selectedVehicleLabel} to ${selectedSwapTaskVehicleLabel}`,
                description:
                  formData.description.trim() || swapDescription,
              }
            : {
                swapVehicleId: null,
                swapVehicleSource: null,
                swapCurrentVehicleLabel: null,
                swapVehicleLabel: null,
                swapSummary: null,
              }),
        };
      }
    }

    console.log("Task Data to submit:", taskData);

    let result;
    if (editMode) {
      result = await updateTask(selectedTask.id, taskData);
    } else {
      result = await createTask(taskData);
    }

    console.log("Result:", result);

    if (result.success) {
      showSnackbar(
        `Task ${editMode ? "updated" : "created"} successfully`,
        "success"
      );
      handleCloseDialog();
    } else {
      showSnackbar(result.error || "Operation failed", "error");
    }
  };

  const handleDelete = async (taskId) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      const result = await deleteTask(taskId);
      if (result.success) {
        showSnackbar("Task deleted successfully", "success");
      } else {
        showSnackbar(result.error || "Failed to delete task", "error");
      }
    }
  };

  const handleCompleteTask = async (taskId) => {
    const result = await updateTask(taskId, { status: "completed" });
    if (result.success) {
      showSnackbar("Task marked as completed", "success");
    } else {
      showSnackbar(result.error || "Failed to update task", "error");
    }
  };

  const handleCancelTask = async (taskId) => {
    if (window.confirm("Are you sure you want to cancel this task?")) {
      const result = await updateTask(taskId, { status: "cancelled" });
      if (result.success) {
        showSnackbar("Task cancelled", "warning");
      } else {
        showSnackbar(result.error || "Failed to cancel task", "error");
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get task statistics
  const activeTasks = tasks.filter(
    (t) => t.status === "assigned" || t.status === "in-progress"
  ).length;
  const completedTasks = tasks.filter((t) => t.status === "completed").length;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 1,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
          Task Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Task
        </Button>
      </Box>

      {/* Task Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          item
          size={{
            xs: 12,
            md: 4,
            lg: 4,
          }}
        >
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #eceff1" }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Total Tasks
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold" }}
            >
              {tasks.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid
          item
          size={{
            xs: 12,
            md: 4,
            lg: 4,
          }}
        >
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #eceff1" }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Active Tasks
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", color: "#ff9800" }}
            >
              {activeTasks}
            </Typography>
          </Paper>
        </Grid>
        <Grid
          item
          size={{
            xs: 12,
            md: 4,
            lg: 4,
          }}
        >
          <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #eceff1" }}>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Completed Tasks
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", color: "#4caf50" }}
            >
              {completedTasks}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tasks Table */}
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}
      >
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fb" }}>
              <TableCell>Driver</TableCell>
              <TableCell>Task Type</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No tasks found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => {
                const isInspectionTaskRow =
                  task.taskType === INSPECTION_TASK_TYPE;
                const isVehicleSwapTaskRow =
                  task.taskType === VEHICLE_SWAP_TASK_TYPE;
                const isTransportTaskRow =
                  (task.taskType || TRANSPORT_TASK_TYPE) ===
                  TRANSPORT_TASK_TYPE;
                const driver = findDriverByIdentifier(drivers, task.driverId);
                const normalizedTaskVehicleSource = normalizeVehicleSource(
                  task.vehicleSource
                );
                const vehicle =
                  normalizedTaskVehicleSource === "own"
                    ? driver?.ownVehicle
                    : vehicles.find((v) => v.id === task.vehicleId);
                const vehicleTypeLabel =
                  normalizedTaskVehicleSource === "own"
                    ? vehicle?.type || task.vehicleType || "Unknown"
                    : vehicle?.vehicleType ||
                      vehicle?.type ||
                      task.vehicleType ||
                      "Unknown";

                return (
                  <TableRow key={task.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {driver?.name || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {driver?.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTaskTypeLabel(task.taskType)}
                        size="small"
                        color={isInspectionTaskRow ? "primary" : "primary"}
                        variant="outlined"
                      />
                      {task.reminderMinutesBefore !== undefined && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mt: 0.5 }}
                        >
                          {task.reminderEnabled === false
                            ? "Reminder: Off"
                            : `Reminder: ${formatReminderIntervalLabel(
                                task.reminderMinutesBefore
                              )} before, every ${formatReminderIntervalLabel(
                                task.reminderRepeatMinutes ||
                                  PRIORITY_DEFAULT_REPEAT_MINUTES[
                                    task.priority || "medium"
                                  ] ||
                                  PRIORITY_DEFAULT_REPEAT_MINUTES.medium
                              )}`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {normalizedTaskVehicleSource === "own" ? (
                        <Box>
                          <Chip
                            label="Own Vehicle"
                            size="small"
                            color="success"
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="body2">
                            {vehicleTypeLabel} - {vehicle?.make} {vehicle?.model}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vehicle?.seats} seats | {vehicle?.plateNumber}
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          <Chip
                            label="Fleet"
                            size="small"
                            color="warning"
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="body2">
                            {vehicleTypeLabel}{" "}
                            - {vehicle?.make} {vehicle?.model}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {vehicle?.capacity || vehicle?.seats} seats |{" "}
                            {vehicle?.licensePlate || vehicle?.plateNumber}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(task.inspectionDate || task.startTime)}
                      </Typography>
                      {isTransportTaskRow && (
                        <Typography variant="caption" color="text.secondary">
                          End: {formatDate(task.endTime)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isTransportTaskRow ? (
                        <>
                          <Typography variant="body2">
                            {task.pickupLocation || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            To: {task.dropoffLocation || "N/A"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            <PersonIcon sx={{ fontSize: 14, mr: 0.25, verticalAlign: "text-bottom" }} />
                            {task.passengers || 1} passenger(s)
                          </Typography>
                        </>
                      ) : isVehicleSwapTaskRow ? (
                        <>
                          <Typography variant="body2">
                            {task.description || "Vehicle swap task"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            Current: {task.swapCurrentVehicleLabel || task.dropoffLocation || "Assigned vehicle"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            Swap with: {task.swapVehicleLabel || "Not selected"}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">
                            {isInspectionTaskRow
                              ? task.inspectionDetails || task.description || "Vehicle inspection"
                              : task.description || getTaskTypeLabel(task.taskType)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Vehicle: {task.dropoffLocation || "Assigned vehicle"}
                          </Typography>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatusLabel(task.priority, "Medium")}
                        color={
                          task.priority === "high"
                            ? "error"
                            : task.priority === "low"
                            ? "default"
                            : "warning"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatStatusLabel(task.status, "Assigned")}
                        color={getStatusColor(task.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        {(task.status === "assigned" ||
                          task.status === "in-progress") && (
                          <>
                            <Tooltip title="Mark Complete">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleCompleteTask(task.id)}
                              >
                                <CompleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel Task">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleCancelTask(task.id)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(task)}
                            disabled={
                              task.status === "completed" ||
                              task.status === "cancelled"
                            }
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(task.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Task Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editMode ? "Edit Task" : "Create New Task"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 0.5 }}>
              Select task type, driver, vehicle, date/time, and reminder before
              creating the task.
            </Alert>

            <Paper sx={{ p: 2, bgcolor: "#fafafa", border: "1px solid #eceff1" }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Task Setup
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Task Type</InputLabel>
                    <Select
                      name="taskType"
                      value={formData.taskType}
                      label="Task Type"
                      onChange={handleInputChange}
                    >
                      {TASK_TYPE_OPTIONS.map((taskTypeOption) => (
                        <MenuItem
                          key={taskTypeOption.value}
                          value={taskTypeOption.value}
                        >
                          {taskTypeOption.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Select Driver</InputLabel>
                    <Select
                      name="driverId"
                      value={formData.driverId}
                      label="Select Driver"
                      onChange={(e) => handleDriverSelect(e.target.value)}
                    >
                      {availableDrivers.length === 0 ? (
                        <MenuItem disabled>No available drivers</MenuItem>
                      ) : (
                        availableDrivers.map((driver) => (
                          <MenuItem key={driver.id} value={driver.id}>
                            {driver.name} - {driver.email}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {!isInspectionTask && (
              <>
                {isTransportTask && (
                  <Paper
                    sx={{ p: 2, bgcolor: "#fafafa", border: "1px solid #eceff1" }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      Booking Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Number of Passengers"
                          name="passengers"
                          type="number"
                          value={formData.passengers}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          InputProps={{ inputProps: { min: 1, max: 20 } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>Vehicle Type Needed</InputLabel>
                          <Select
                            name="vehicleType"
                            value={formData.vehicleType}
                            label="Vehicle Type Needed"
                            onChange={handleInputChange}
                          >
                            <MenuItem value="Any">Any</MenuItem>
                            {VEHICLE_TYPES.map((vehicleType) => (
                              <MenuItem key={vehicleType} value={vehicleType}>
                                {vehicleType}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {isTransportTask && (
                  <Paper
                    sx={{ p: 2, bgcolor: "#fcfcfc", border: "1px solid #eceff1" }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      <LocationIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                      Location Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <TextField
                          label="Pickup Location"
                          name="pickupLocation"
                          value={formData.pickupLocation}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          placeholder="Enter pickup address or location"
                        />
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          label="Drop-off Location"
                          name="dropoffLocation"
                          value={formData.dropoffLocation}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          placeholder="Enter drop-off address or location"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {selectedDriver && (
                  <Card
                    sx={{
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Avatar
                          sx={{ bgcolor: "#DD2424", mr: 2, width: 48, height: 48 }}
                        >
                          {selectedDriver.name?.substring(0, 2).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {selectedDriver.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedDriver.email}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <FormControl fullWidth required>
                            <InputLabel>Vehicle Source</InputLabel>
                            <Select
                              value={selectedVehicleChoice}
                              label="Vehicle Source"
                              onChange={(e) => handleVehicleChoice(e.target.value)}
                            >
                              {selectedDriver.ownVehicle?.type && (
                                <MenuItem value="own">
                                  Driver Own Vehicle ({selectedDriver.ownVehicle.type})
                                </MenuItem>
                              )}
                              <MenuItem value="warehouse">Fleet Vehicle</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {selectedVehicleChoice === "warehouse" && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth required>
                              <InputLabel>Select Fleet Vehicle</InputLabel>
                              <Select
                                name="vehicleId"
                                value={formData.vehicleId}
                                label="Select Fleet Vehicle"
                                onChange={handleInputChange}
                              >
                                {warehouseVehicleSelectOptions.length === 0 && (
                                  <MenuItem disabled>
                                    No matching fleet vehicles found
                                  </MenuItem>
                                )}
                                {warehouseVehicleSelectOptions.map((vehicle) => {
                                  const seats = vehicle.capacity || vehicle.seats;
                                  const type = vehicle.vehicleType || vehicle.type;
                                  const plate =
                                    vehicle.licensePlate || vehicle.plateNumber;
                                  const status = getVehicleEffectiveStatus(
                                    vehicle,
                                    tasks,
                                    { lockHours: 4 }
                                  );
                                  const statusLabel = formatStatusLabel(
                                    status,
                                    "Available"
                                  );

                                  return (
                                    <MenuItem key={vehicle.id} value={vehicle.id}>
                                      {type} - {vehicle.make} {vehicle.model} ({seats} seats) - {plate} [{statusLabel}]
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {isVehicleSwapTask && (
                          <Grid size={12}>
                            <Alert severity="info">
                              Current vehicle: {formData.vehicleId ? currentVehicleLabel : "Select current vehicle first"}
                            </Alert>
                          </Grid>
                        )}

                        {isVehicleSwapTask && (
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth required>
                              <InputLabel>Swap With Fleet Vehicle</InputLabel>
                              <Select
                                name="swapVehicleId"
                                value={formData.swapVehicleId}
                                label="Swap With Fleet Vehicle"
                                onChange={handleInputChange}
                              >
                                {swapVehicleSelectOptions.length === 0 && (
                                  <MenuItem disabled>
                                    No swap vehicles available
                                  </MenuItem>
                                )}
                                {swapVehicleSelectOptions.map((vehicle) => {
                                  const type = vehicle.vehicleType || vehicle.type;
                                  const plate =
                                    vehicle.licensePlate || vehicle.plateNumber;
                                  const status = getVehicleEffectiveStatus(
                                    vehicle,
                                    tasks,
                                    { lockHours: 4 }
                                  );
                                  const statusLabel = formatStatusLabel(
                                    status,
                                    "Available"
                                  );

                                  return (
                                    <MenuItem key={vehicle.id} value={vehicle.id}>
                                      {type} - {vehicle.make} {vehicle.model} ({plate}) [{statusLabel}]
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>

                      {selectedVehicleChoice === "warehouse" &&
                        warehouseVehicleSelectOptions.length === 0 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            No fleet vehicle matches this selection. Try changing
                            passenger count, vehicle type, or selecting a
                            different driver.
                          </Alert>
                        )}

                      {isVehicleSwapTask &&
                        swapVehicleSelectOptions.length === 0 && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            No fleet vehicle is available to select for the swap.
                          </Alert>
                        )}

                      {isVehicleSwapTask && formData.swapVehicleId && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                          Swap plan: {formData.vehicleId ? currentVehicleLabel : "Current vehicle"} to{" "}
                          {selectedSwapVehicleLabel}
                        </Alert>
                      )}

                      {selectedVehicleChoice === "own" &&
                        selectedDriver.ownVehicle?.type && (
                          <Alert severity="success" sx={{ mt: 2 }}>
                            Own vehicle selected: {selectedDriver.ownVehicle.type} -{" "}
                            {selectedDriver.ownVehicle.make} {selectedDriver.ownVehicle.model}
                          </Alert>
                        )}

                      {selectedVehicleChoice === "own" &&
                        selectedDriverOwnVehicleAvailable &&
                        formData.vehicleSource === "own" &&
                        autoSelectedVehicleHint === "own" && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            Own vehicle auto-selected for this driver. You can
                            change it from the Fleet dropdown.
                          </Alert>
                        )}

                      {selectedVehicleChoice === "warehouse" &&
                        selectedDriverAssignedFleetVehicleAvailable &&
                        formData.vehicleId ===
                          selectedDriverAssignedFleetVehicle?.id && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            Assigned fleet vehicle auto-selected for this driver.
                            You can change it from the Fleet dropdown.
                          </Alert>
                        )}
                    </CardContent>
                  </Card>
                )}

                <Paper sx={{ p: 2, bgcolor: "#fafafa", border: "1px solid #eceff1" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Schedule, Reminder & Priority
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: isTransportTask ? 4 : 4 }}>
                      <TextField
                        label={isTransportTask ? "Start Time" : "Task Date & Time"}
                        name="startTime"
                        type="datetime-local"
                        value={formData.startTime}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    {isTransportTask && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          label="End Time"
                          name="endTime"
                          type="datetime-local"
                          value={formData.endTime}
                          onChange={handleInputChange}
                          fullWidth
                          required
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, md: isTransportTask ? 4 : 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Reminder Notifications</InputLabel>
                        <Select
                          name="reminderEnabled"
                          value={formData.reminderEnabled}
                          label="Reminder Notifications"
                          onChange={handleInputChange}
                        >
                          <MenuItem value="yes">Enabled</MenuItem>
                          <MenuItem value="no">Paused by Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: isTransportTask ? 4 : 4 }}>
                      <FormControl fullWidth required disabled={!reminderEnabled}>
                        <InputLabel>First Reminder</InputLabel>
                        <Select
                          name="reminderMinutesBefore"
                          value={formData.reminderMinutesBefore}
                          label="First Reminder"
                          onChange={handleInputChange}
                        >
                          {REMINDER_BEFORE_OPTIONS.map((minutes) => (
                            <MenuItem key={minutes} value={String(minutes)}>
                              {`${formatReminderIntervalLabel(minutes)} before`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: isTransportTask ? 4 : 4 }}>
                      <FormControl fullWidth required disabled={!reminderEnabled}>
                        <InputLabel>Repeat Reminder Every</InputLabel>
                        <Select
                          name="reminderRepeatMinutes"
                          value={formData.reminderRepeatMinutes}
                          label="Repeat Reminder Every"
                          onChange={handleInputChange}
                        >
                          {REMINDER_REPEAT_OPTIONS.map((minutes) => (
                            <MenuItem key={minutes} value={String(minutes)}>
                              {`Every ${formatReminderIntervalLabel(minutes)}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: isTransportTask ? 12 : 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          name="priority"
                          value={formData.priority}
                          label="Priority"
                          onChange={handleInputChange}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {!isTransportTask && (
                      <Grid size={12}>
                        <Alert severity="info">
                          Driver, vehicle, date/time, and reminder are required
                          for this task type. Priority presets: High (15 min),
                          Medium (2 hours), Low (2 days) for repeat reminders.
                        </Alert>
                      </Grid>
                    )}
                    {!reminderEnabled && (
                      <Grid size={12}>
                        <Alert severity="warning">
                          Reminder notifications are paused for this task by
                          admin.
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: "#fcfcfc", border: "1px solid #eceff1" }}>
                  <TextField
                    label={isTransportTask ? "Additional Notes" : "Task Details"}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                    required={isMaintenanceStyleTask}
                    placeholder={
                      isTransportTask
                        ? "Any special instructions or notes..."
                        : "Describe what needs to be done for this task"
                    }
                  />
                </Paper>
              </>
            )}

            {isInspectionTask && (
              <>
                <Paper sx={{ p: 2, bgcolor: "#fafafa", border: "1px solid #eceff1" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Vehicle Inspection Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <TextField
                        label="Inspection Details"
                        name="inspectionDetails"
                        value={formData.inspectionDetails}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        multiline
                        rows={3}
                        placeholder="What should the driver inspect?"
                      />
                    </Grid>
                    <Grid size={12}>
                      <FormControl fullWidth required disabled={!selectedDriver}>
                        <InputLabel>Select Vehicle</InputLabel>
                        <Select
                          value={inspectionVehicleSelectValue}
                          label="Select Vehicle"
                          onChange={(e) =>
                            handleInspectionVehicleSelect(e.target.value)
                          }
                        >
                          {selectedDriver?.ownVehicle?.type && (
                            <MenuItem
                              value={`own:${selectedDriverUserId || selectedDriver.id}`}
                            >
                              Own Vehicle - {selectedDriver.ownVehicle.type} -{" "}
                              {selectedDriver.ownVehicle.make} {selectedDriver.ownVehicle.model}
                            </MenuItem>
                          )}
                          {vehicles.map((vehicle) => {
                            const type = vehicle.vehicleType || vehicle.type;
                            const plate = vehicle.licensePlate || vehicle.plateNumber;
                            return (
                              <MenuItem
                                key={vehicle.id}
                                value={`warehouse:${vehicle.id}`}
                              >
                                Fleet - {type} - {vehicle.make} {vehicle.model} ({plate})
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Grid>
                    {!selectedDriver && (
                      <Grid size={12}>
                        <Alert severity="info">
                          Select a driver first to choose an inspection vehicle.
                        </Alert>
                      </Grid>
                    )}
                    {selectedDriverAssignedFleetVehicleAvailable &&
                      formData.inspectionVehicleSource === "warehouse" &&
                      formData.inspectionVehicleId ===
                        selectedDriverAssignedFleetVehicle?.id && (
                        <Grid size={12}>
                          <Alert severity="info">
                            Assigned fleet vehicle auto-selected for inspection.
                            You can change it from the Fleet list.
                          </Alert>
                        </Grid>
                      )}
                    {selectedDriverOwnVehicleAvailable &&
                      formData.inspectionVehicleSource === "own" &&
                      autoSelectedVehicleHint === "own" && (
                        <Grid size={12}>
                          <Alert severity="info">
                            Own vehicle auto-selected for inspection. You can
                            change it from the Fleet list.
                          </Alert>
                        </Grid>
                      )}
                  </Grid>
                </Paper>

                <Paper sx={{ p: 2, bgcolor: "#fcfcfc", border: "1px solid #eceff1" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, fontWeight: 600 }}
                  >
                    Inspection Schedule, Reminder & Priority
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Inspection Date"
                        name="inspectionDate"
                        type="datetime-local"
                        value={formData.inspectionDate}
                        onChange={handleInputChange}
                        fullWidth
                        required
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Reminder Notifications</InputLabel>
                        <Select
                          name="reminderEnabled"
                          value={formData.reminderEnabled}
                          label="Reminder Notifications"
                          onChange={handleInputChange}
                        >
                          <MenuItem value="yes">Enabled</MenuItem>
                          <MenuItem value="no">Paused by Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select
                          name="priority"
                          value={formData.priority}
                          label="Priority"
                          onChange={handleInputChange}
                        >
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth required disabled={!reminderEnabled}>
                        <InputLabel>First Reminder</InputLabel>
                        <Select
                          name="reminderMinutesBefore"
                          value={formData.reminderMinutesBefore}
                          label="First Reminder"
                          onChange={handleInputChange}
                        >
                          {REMINDER_BEFORE_OPTIONS.map((minutes) => (
                            <MenuItem key={minutes} value={String(minutes)}>
                              {`${formatReminderIntervalLabel(minutes)} before`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth required disabled={!reminderEnabled}>
                        <InputLabel>Repeat Reminder Every</InputLabel>
                        <Select
                          name="reminderRepeatMinutes"
                          value={formData.reminderRepeatMinutes}
                          label="Repeat Reminder Every"
                          onChange={handleInputChange}
                        >
                          {REMINDER_REPEAT_OPTIONS.map((minutes) => (
                            <MenuItem key={minutes} value={String(minutes)}>
                              {`Every ${formatReminderIntervalLabel(minutes)}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={12}>
                      <Alert severity="info" sx={{ height: "100%" }}>
                        Default reminder setup is 15 minutes before and every
                        2 hours after due time until task start. You can adjust
                        this per task.
                      </Alert>
                    </Grid>
                    {!reminderEnabled && (
                      <Grid size={12}>
                        <Alert severity="warning">
                          Reminder notifications are paused for this task by
                          admin.
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitDisabled}
          >
            {editMode
              ? "Update Task"
              : `Create ${getTaskTypeLabel(formData.taskType)}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Tasks;
